import {useMemo} from 'react'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useQuery} from '@github-ui/react-query'
import {useSearchParams, useParams} from '@github-ui/react-router'
import {pullsDashboardSurfaceContentRoute} from '../routes/pulls-dashboard-surface-content-route'
import {PULLS_PATH} from '../constants/routes'
import {queryResolver} from '../helpers/query-resolver'
import {deferredPullRequestIdParam} from '../helpers/section-fetchers'
import type {PullsDashboardDeferredDataPayload, PullsDashboardEnrichedDataPayload} from '../types/routes-types'
import type {DeferredItem} from '@github-ui/shared-dashboard-lists/pulls/types/list-types'
import {getListItemIdentity} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'

const EMPTY_RESULTS: DeferredItem[] = []

function toMapByIdentity(results: DeferredItem[]) {
  const map = new Map<string, DeferredItem>()
  for (const result of results) map.set(getListItemIdentity({...result, itemType: 'pull_request'}), result)
  return map
}

export function useEnrichedPulls(): PullsDashboardEnrichedDataPayload {
  const [searchParams] = useSearchParams()
  const {mode} = useParams<{mode?: string}>()
  const mainQuery = useSuspenseRouteQuery(pullsDashboardSurfaceContentRoute, 'mainQuery')
  const query = queryResolver({searchParams, params: {mode}})
  const page = Number(searchParams.get('page') || 1)

  const mainResults = mainQuery.data?.results ?? EMPTY_RESULTS
  const deferredQueryDeps = useMemo(() => {
    const nextSearchParams: Array<[string, string]> = [
      ['q', query],
      ['page', String(page)],
    ]

    for (const pull of mainResults) {
      nextSearchParams.push(deferredPullRequestIdParam(pull.id))
    }

    return {
      pathname: `${PULLS_PATH}/deferred`,
      searchParams: nextSearchParams,
    }
  }, [mainResults, page, query])

  const deferredQueryKey = useMemo(
    () => ['dashboard-surface', 'pulls', 'deferredEnrichedPulls', deferredQueryDeps],
    [deferredQueryDeps],
  )

  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister -- depends on in-memory main query results (pr_ids)
  const deferredQuery = useQuery({
    queryKey: deferredQueryKey,
    queryFn: ({signal}) => {
      return queryFnFetch<PullsDashboardDeferredDataPayload>({
        queryDeps: {
          ...deferredQueryDeps,
          init: {signal},
        },
      })
    },
    enabled: mainResults.length > 0,
  })

  const deferredResults = deferredQuery.data?.payload?.pullsDashboardSurfaceContentDeferredData.results

  const enrichedPullsData = useMemo(() => {
    const deferredByIdentity = toMapByIdentity(deferredResults || [])
    return mainResults.map(pull => {
      const deferredPull = deferredByIdentity.get(getListItemIdentity({...pull, itemType: 'pull_request'}))
      return {
        ...pull,
        reviewDecisionState: deferredPull?.reviewDecisionState,
        statusCheckRollup: deferredPull?.statusCheckRollup,
        approvedReviewsCount: deferredPull?.approvedReviewsCount,
        changesRequestedReviewsCount: deferredPull?.changesRequestedReviewsCount,
      }
    })
  }, [deferredResults, mainResults])

  return {
    pulls: enrichedPullsData,
    pageInfo: mainQuery?.data?.pageInfo,
    isFetching: mainQuery.isFetching,
    isPending: mainResults.length > 0 && deferredQuery.isPending,
    failedRefetch: mainQuery.isRefetchError ? {query, page, updateCount: mainQuery.errorUpdateCount} : undefined,
    retryFailedRefetch: mainQuery.refetch,
    // Initial errors suspend this query; cached refetch errors are reported separately above.
    error: mainQuery.data.error || null,
  }
}
